import { useState, useEffect } from 'react';
import { Bell, BellRinging, Check, Trash, Clock, TrendUp, TrendDown, X, Printer, SpinnerGap } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { printReport, formatCurrency } from '../utils/printReport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Componente para mostrar el historial de alertas de precio disparadas
 */
const AlertHistory = ({ isOpen, onClose }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);

  // Cargar historial de alertas
  const fetchAlertHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/alerts/history`);
      setAlerts(response.data.alerts || []);
      setUnreadCount(response.data.unread_count || 0);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching alert history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAlertHistory();
    }
  }, [isOpen]);

  // Marcar alerta como leída
  const markAsRead = async (ticker) => {
    try {
      await axios.put(`${API}/alerts/history/${ticker}/read`);
      fetchAlertHistory();
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  // Marcar todas como leídas
  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/alerts/history/read-all`);
      fetchAlertHistory();
    } catch (error) {
      console.error('Error marking all alerts as read:', error);
    }
  };

  // Limpiar historial
  const clearHistory = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar todo el historial de alertas?')) {
      return;
    }
    try {
      await axios.delete(`${API}/alerts/history/clear`);
      fetchAlertHistory();
    } catch (error) {
      console.error('Error clearing alert history:', error);
    }
  };

  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Fecha desconocida';
    const date = new Date(dateStr);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Exportar a PDF usando printReport unificado
  const exportToPDF = () => {
    if (alerts.length === 0) {
      toast.error('No hay alertas para exportar');
      return;
    }
    
    const aboveCount = alerts.filter(a => a.type === 'above').length;
    const belowCount = alerts.filter(a => a.type === 'below').length;
    
    printReport({
      title: 'Historial de Alertas',
      subtitle: `${total} alertas registradas`,
      icon: '🔔',
      accentColor: '#f59e0b',
      summaryCards: [
        { value: String(total), label: 'Total Alertas', color: '#f59e0b' },
        { value: String(aboveCount), label: 'Por Encima', color: '#22c55e' },
        { value: String(belowCount), label: 'Por Debajo', color: '#ef4444' },
        { value: String(unreadCount), label: 'Sin Leer', color: '#06b6d4' }
      ],
      tableHeaders: ['Ticker', 'Tipo', 'Precio Objetivo', 'Precio Actual', 'Fecha'],
      tableRows: alerts.map(alert => [
        `<strong style="color: #f59e0b;">${alert.ticker}</strong>`,
        `<span class="${alert.type === 'above' ? 'positive' : 'negative'}">${alert.type === 'above' ? '↑ Por encima' : '↓ Por debajo'}</span>`,
        formatCurrency(alert.target_price),
        formatCurrency(alert.current_price),
        formatDate(alert.triggered_at)
      ]),
      footnote: 'Las alertas son solo informativas y no constituyen asesoramiento financiero.'
    });
    
    toast.success('Informe abierto para impresión');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700 bg-gradient-to-r from-gold-500/20 to-orange-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gold-500/20 rounded-lg">
              <BellRinging size={24} className="text-gold-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Historial de Alertas</h2>
              <p className="text-sm text-zinc-400">
                {total} alertas • {unreadCount} sin leer
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {alerts.length > 0 && (
              <button
                onClick={exportToPDF}
                className="px-3 py-1.5 bg-gold-600/20 hover:bg-gold-600/40 text-gold-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                title="Imprimir historial"
              >
                <Printer size={16} />
                Imprimir
              </button>
            )}
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-3 py-1.5 bg-green-600/20 hover:bg-green-600/40 text-green-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
              >
                <Check size={16} />
                Marcar todas leídas
              </button>
            )}
            {total > 0 && (
              <button
                onClick={clearHistory}
                className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
              >
                <Trash size={16} />
                Limpiar
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500"></div>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12">
              <Bell size={48} className="mx-auto text-zinc-600 mb-3" />
              <p className="text-zinc-400 text-lg">No hay alertas en el historial</p>
              <p className="text-zinc-500 text-sm mt-1">
                Cuando se dispare una alerta de precio, aparecerá aquí
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert, index) => (
                <div
                  key={`${alert.ticker}-${index}`}
                  className={`p-4 rounded-xl border transition-all ${
                    alert.read
                      ? 'bg-zinc-800/50 border-zinc-700/50 opacity-70'
                      : 'bg-gradient-to-r from-gold-500/10 to-orange-500/10 border-gold-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        alert.condition === 'above' 
                          ? 'bg-green-500/20' 
                          : 'bg-red-500/20'
                      }`}>
                        {alert.condition === 'above' 
                          ? <TrendUp size={20} className="text-green-400" />
                          : <TrendDown size={20} className="text-red-400" />
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white text-lg">{alert.ticker}</span>
                          {!alert.read && (
                            <span className="px-2 py-0.5 bg-gold-500 text-black text-xs font-bold rounded-full">
                              NUEVA
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-400">
                          {alert.condition === 'above' ? 'Superó' : 'Bajó de'} objetivo: {alert.target_price?.toFixed(2)}€
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-mono font-bold ${
                        alert.condition === 'above' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {alert.triggered_price?.toFixed(2)}€
                      </div>
                      <div className="flex items-center gap-1 text-xs text-zinc-500">
                        <Clock size={12} />
                        {formatDate(alert.triggered_at)}
                      </div>
                    </div>
                  </div>
                  {!alert.read && (
                    <div className="mt-3 pt-3 border-t border-zinc-700/50">
                      <button
                        onClick={() => markAsRead(alert.ticker)}
                        className="text-sm text-gold-400 hover:text-gold-300 font-medium flex items-center gap-1"
                      >
                        <Check size={14} />
                        Marcar como leída
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Hook para obtener el contador de alertas no leídas
 */
export const useAlertHistoryCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchCount = async () => {
    try {
      const response = await axios.get(`${API}/alerts/history?limit=1`);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching alert count:', error);
    }
  };

  useEffect(() => {
    fetchCount();
    // Actualizar cada 30 segundos
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  return { unreadCount, refresh: fetchCount };
};

export default AlertHistory;
